import styled from 'styled-components'
import { colors } from 'src/Global/Styles/variables'

import { CustomerCustomComponents } from '../customerCustomComponents'
import { InitialButton as DefaultInitialButton } from '../../../Buttons'
import {
  InfoContainer as DefaultInfoContainer,
  InitialButtonContainer as DefaultInitialButtonContainer,
  ModalContainer as DefaultModalContainer,
  PatientUploadContainer as DefaultPatientUploadContainer,
  PatientVerificationContainer as DefaultPatientVerificationContainer,
} from '../../../Containers'
import { HeaderContainer as DefaultHeaderContainer } from '../../../Headers/defaultComponents'
import {
  Layer as DefaultLayer,
  Modal as DefaultModal,
} from '../../../../../../Components/Common/ModalLayer/defaultComponents'
import {
  UploadBoxContainer as DefaultUploadBoxContainer,
  UploadButton as DefaultUploadButton,
  UploadFileInfo as DefaultUploadFileInfo,
  ImageCard as DefaultImageCard,
  StyledDeleteButton as DefaultStyledDeleteButton,
  ImageMetaInfo as DefaultImageMetaInfo,
} from '../../../Upload/defaultComponents'

/**
 * This file defines the components and styles that are specific to this customer.
 * These custom components are based on parent components, which are used by default.
 */

/**
 * Customer logo
 */
export { default as PfizerMerckLogo } from './logo.svg'

/**
 * Common variables
 */
const titleLetterSpacing = '-0.03125rem'

/**
 * Button components
 */
const InitialButton = styled(DefaultInitialButton)`
  padding: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    width: 30%;
  }

  ${(props) => {
    const commonStyle = `
    font-family: ${props.theme.mainFontTheme};
    letter-spacing: ${titleLetterSpacing};
    border-radius: 0.5rem;
    font-size: 1rem;
    font-weight: 700;
  `
    const conditionalStyle = () =>
      props.filled
        ? `
      background-color: ${
        props.disabled ? 'rgba(3, 77, 131, 0.3)' : props.theme.mainThemeColor
      };
      color: white;
      `
        : `
      background-color: ${colors.white};
      color: ${colors.congressBlue};
      border: 0.125rem solid ${colors.congressBlue};
    `

    const hoverStyle = () =>
      !props.disabled
        ? ` 
        &:hover {
          transition: all 0.3s ease;
          color: ${colors.white};
          border-color: transparent;
          background-color: #00253F;
        }
      `
        : null

    return commonStyle + conditionalStyle() + hoverStyle()
  }};
`

const NextButton = styled(InitialButton)`
  width: 100%;
  max-width: 175px;

  @media (min-width: 375px) {
    max-width: 200px;
  }

  @media (min-width: 768px) {
    max-width: 300px;
  }
`

const BackButton = styled(InitialButton)`
  width: 100%;
  max-width: 100px;

  @media (min-width: 375px) {
    max-width: 125px;
  }

  @media (min-width: 768px) {
    max-width: 150px;
  }
`

const ResendButton = styled(InitialButton)`
  width: 100%;
  margin-top: 1rem;
`

const TakePhotoButton = styled(InitialButton)`
  width: 100%;
`

/**
 * Container components
 */
const InfoContainer = styled(DefaultInfoContainer)`
  h2 {
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 150%;
  }

  li {
    font-size: 1.125rem;
    line-height: 150%;
  }

  @media (min-width: 768px) {
    h2 {
      letter-spacing: ${titleLetterSpacing};
      font-size: 1.25rem;
    }

    li {
      letter-spacing: -0.25px;
      font-size: 1.25rem;
    }
  }
`

const InitialButtonContainer = styled(DefaultInitialButtonContainer)`
  margin-top: 2rem;

  @media (min-width: 768px) {
    justify-content: end;
  }
`

const ModalContainer = styled(DefaultModalContainer)`
  h2 {
    font-weight: 700;
    letter-spacing: ${titleLetterSpacing};
    color: ${colors.blackPearl};
    line-height: 130%;
  }

  p {
    color: ${colors.brightGrey};
    line-height: 150%;
  }
`

const PatientUploadContainer = styled(DefaultPatientUploadContainer)`
  padding: 1.5rem;
`

const PatientVerificationContainer = styled(
  DefaultPatientVerificationContainer
)`
  max-width: 330px;

  h2 {
    color: ${colors.blackPearl};
    font-weight: 700;
    font-size: 1.875;
    margin-bottom: 2rem;
    letter-spacing: ${titleLetterSpacing};
    line-height: 130%;
  }

  h3,
  p {
    font-size: 1rem;
    line-height: 150%;
  }

  h3 {
    color: ${colors.blackPearl};
    margin: 0 0 1rem 0;
    font-weight: 700;
  }

  @media (min-width: 768px) {
    max-width: 600px;
    h2 {
      font-size: 2.25rem;
    }
  }
`

/**
 * Headers components
 */
const HeaderContainer = styled(DefaultHeaderContainer)`
  h2 {
    color: ${colors.blackPearl};
    margin-bottom: 1.5rem;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: ${titleLetterSpacing};
    line-height: 130%;
  }

  p {
    color: ${colors.brightGrey};
    margin-bottom: 2rem;
    font-size: 1rem;
    line-height: 150%;
  }

  @media (min-width: 768px) {
    h2 {
      font-size: 1.5rem;
    }
  }
`

/**
 * Layer components
 */
const Layer = styled(DefaultLayer)`
  background-color: ${colors.gullGrey};
  padding: 1rem;
`

const Modal = styled(DefaultModal)`
  box-shadow: 0 0.375rem 0.5rem -6px rgba(24, 39, 75, 0.12),
    0 0.5rem 1rem -6px rgba(24, 39, 75, 0.08);
  border-radius: 1rem;
`

/**
 * Upload components
 */
const UploadBoxContainer = styled(DefaultUploadBoxContainer)`
  background-color: #f9fafb;
  min-height: 11rem;
  border: 0.125rem dashed ${colors.gullGrey};
  border-radius: 32px;
  padding: 2rem;
`

const UploadButton = styled(DefaultUploadButton)`
  color: ${colors.congressBlue};
  font-weight: 700;
`

const UploadFileInfo = styled(DefaultUploadFileInfo)`
  p {
    line-height: 150%;
  }
`

const ImageCard = styled(DefaultImageCard)`
  margin-bottom: 2rem;
  height: 6.31rem;
  display: grid;
  border: 1px solid ${colors.athensGray};
  border-radius: 32px;
  box-shadow: 0 0.125rem 0.25rem -2px rgba(24, 39, 75, 0.12),
    0 0.25rem 0.25rem -2px rgba(24, 39, 75, 0.08);
`

const StyledDeleteButton = styled(DefaultStyledDeleteButton)`
  max-width: 2.25rem;
  padding-right: 1rem;
`

const ImageMetaInfo = styled(DefaultImageMetaInfo)`
  img {
    max-width: 5.875rem;
    padding-right: 1rem;
    padding-left: 1rem;
  }

  p {
    line-height: 150%;
  }
`

export const pfizerMerckCustomComponents: CustomerCustomComponents = {
  // Buttons
  InitialButton,
  NextButton,
  BackButton,
  ResendButton,
  TakePhotoButton,
  // Containers
  InfoContainer,
  InitialButtonContainer,
  ModalContainer,
  PatientUploadContainer,
  PatientVerificationContainer,
  // Headers
  HeaderContainer,
  // Modals
  Layer,
  Modal,
  // Upload section
  UploadBoxContainer,
  UploadButton,
  UploadFileInfo,
  ImageCard,
  StyledDeleteButton,
  ImageMetaInfo,
}
